import { API_BASE } from '@/shared/services/api';
import { useTranslation } from "react-i18next";import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/shared/components/ui/card';
import { Button } from '@/shared/components/ui/button';
import { Badge } from '@/shared/components/ui/badge';
import { Progress } from '@/shared/components/ui/progress';
import { ScrollArea } from '@/shared/components/ui/scroll-area';
import { Input } from '@/shared/components/ui/input';
import { Label } from '@/shared/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger } from
'@/shared/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue } from
'@/shared/components/ui/select';
import {
  Thermometer,
  AlertTriangle,
  CheckCircle2,
  AlertCircle,
  RefreshCw,
  Plus,
  History,
  Settings,
  Snowflake,
  Droplets,
  Battery,
  Signal,
  Clock,
  MapPin } from
'lucide-react';
import { toast } from 'sonner';

const API_URL = API_BASE || '';
const ezerpToken = localStorage.getItem('ezerpToken');

// Temperature zone configurations
const ZONE_CONFIG = {
  frozen: { label: 'Frozen', color: 'bg-blue-500', icon: Snowflake, range: '-25°C to -15°C' },
  refrigerated: { label: 'Refrigerated', color: 'bg-cyan-500', icon: Thermometer, range: '2°C to 8°C' },
  cool: { label: 'Cool', color: 'bg-teal-500', icon: Thermometer, range: '8°C to 15°C' },
  ambient: { label: 'Ambient', color: 'bg-green-500', icon: Thermometer, range: '15°C to 25°C' }
};

const SEVERITY_CONFIG = {
  critical: { color: 'bg-red-500', textColor: 'text-red-500', label: 'Critical' },
  major: { color: 'bg-orange-500', textColor: 'text-orange-500', label: 'Major' },
  minor: { color: 'bg-yellow-500', textColor: 'text-yellow-500', label: 'Minor' },
  warning: { color: 'bg-blue-500', textColor: 'text-blue-500', label: 'Warning' }
};

const ColdChain = () => {const { t } = useTranslation("wms");
  const [sensors, setSensors] = useState([]);
  const [excursions, setExcursions] = useState([]);
  const [stats, setStats] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [showAddSensor, setShowAddSensor] = useState(false);
  const [selectedSensor, setSelectedSensor] = useState(null);
  const [sensorHistory, setSensorHistory] = useState([]);
  const [wsConnected, setWsConnected] = useState(false);

  // Sensor form state
  const [sensorForm, setSensorForm] = useState({
    sensor_id: '',
    name: '',
    location: '',
    zone_type: 'refrigerated',
    min_temp: 2,
    max_temp: 8,
    warning_threshold: 1,
    alert_email: '',
    alert_sms: ''
  });

  // Load data
  const loadData = useCallback(async () => {
    const headers = {
      'Authorization': `Bearer ${ezerpToken}`,
      'Content-Type': 'application/json'
    };
    try {
      const [sensorsRes, excursionsRes, statsRes] = await Promise.all([
      fetch(`${API_URL}/api/coldchain/sensors`, { headers }),
      fetch(`${API_URL}/api/coldchain/excursions?active_only=true`, { headers }),
      fetch(`${API_URL}/api/coldchain/dashboard`, { headers })]
      );

      if (sensorsRes.ok) {
        const data = await sensorsRes.json();
        setSensors(data.sensors || []);
      }

      if (excursionsRes.ok) {
        const data = await excursionsRes.json();
        setExcursions(data.excursions || []);
      }

      if (statsRes.ok) {
        const data = await statsRes.json();
        setStats(data);
      }
    } catch (error) {
      console.error('Failed to load cold chain data:', error);
    }
  }, []);

  // WebSocket connection for real-time updates
  useEffect(() => {
    loadData();

    // Poll for updates every 10 seconds
    const interval = setInterval(loadData, 10000);

    return () => clearInterval(interval);
  }, [loadData]);

  // Create sensor
  const handleCreateSensor = async () => {
    setIsLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/coldchain/sensors`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${ezerpToken}`
        },
        body: JSON.stringify(sensorForm)
      });

      if (response.ok) {
        toast.success('Sensor Created', { description: `${sensorForm.name} added successfully` });
        setShowAddSensor(false);
        loadData();
        setSensorForm({
          sensor_id: '',
          name: '',
          location: '',
          zone_type: 'refrigerated',
          min_temp: 2,
          max_temp: 8,
          warning_threshold: 1,
          alert_email: '',
          alert_sms: ''
        });
      } else {
        const error = await response.json();
        toast.error('Failed to create sensor', { description: error.detail });
      }
    } catch (error) {
      toast.error('Error creating sensor');
    } finally {
      setIsLoading(false);
    }
  };

  // Load sensor history
  const loadSensorHistory = async (sensorId) => {
    try {
      const response = await fetch(`${API_URL}/api/coldchain/sensors/${sensorId}/history?hours=24`, {
        headers: { 'Authorization': `Bearer ${ezerpToken}` }
      });
      if (response.ok) {
        const data = await response.json();
        setSensorHistory(data.readings || []);
      }
    } catch (error) {
      console.error('Failed to load sensor history:', error);
    }
  };

  // Acknowledge excursion
  const acknowledgeExcursion = async (excursionId) => {
    try {
      const response = await fetch(`${API_URL}/api/coldchain/excursions/${excursionId}/acknowledge`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${ezerpToken}`
        },
        body: JSON.stringify({
          user: 'admin',
          corrective_action: 'Investigating temperature deviation'
        })
      });

      if (response.ok) {
        toast.success('Excursion Acknowledged');
        loadData();
      }
    } catch (error) {
      toast.error('Failed to acknowledge excursion');
    }
  };

  // Get status color
  const getSensorStatus = (sensor) => {
    if (sensor.in_excursion) return { color: 'bg-red-500', label: 'Excursion', icon: AlertCircle };
    if (!sensor.current?.temperature) return { color: 'bg-gray-400', label: 'No Data', icon: Clock };

    const temp = sensor.current.temperature;
    const warningLow = sensor.limits.min + (sensor.limits.warning_threshold || 1);
    const warningHigh = sensor.limits.max - (sensor.limits.warning_threshold || 1);

    if (temp < warningLow || temp > warningHigh) {
      return { color: 'bg-yellow-500', label: 'Warning', icon: AlertTriangle };
    }
    return { color: 'bg-green-500', label: 'Normal', icon: CheckCircle2 };
  };

  return (
    <div className="p-6 space-y-6" data-testid="coldchain-dashboard">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Thermometer className="h-6 w-6 text-cyan-500" />{t("coldChainMonitoring", "Cold Chain Monitoring")}

          </h1>
          <p className="text-muted-foreground">{t("realtimeTemperatureMonitoring", "Real-time temperature monitoring and alerts")}</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={loadData}>
            <RefreshCw className="h-4 w-4 mr-2" />{t("refresh", "Refresh")}

          </Button>
          <Dialog open={showAddSensor} onOpenChange={setShowAddSensor}>
            <DialogTrigger asChild>
              <Button>
                <Plus className="h-4 w-4 mr-2" />{t("addSensor", "Add Sensor")}

              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-md">
              <DialogHeader>
                <DialogTitle>{t("addTemperatureSensor", "Add Temperature Sensor")}</DialogTitle>
              </DialogHeader>
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>{t("sensorId", "Sensor ID")}</Label>
                    <Input
                      value={sensorForm.sensor_id}
                      onChange={(e) => setSensorForm({ ...sensorForm, sensor_id: e.target.value })}
                      placeholder="TEMP-001" />
                    
                  </div>
                  <div>
                    <Label>{t("name", "Name")}</Label>
                    <Input
                      value={sensorForm.name}
                      onChange={(e) => setSensorForm({ ...sensorForm, name: e.target.value })}
                      placeholder="Cold Room A" />
                    
                  </div>
                </div>
                <div>
                  <Label>{t("location", "Location")}</Label>
                  <Input
                    value={sensorForm.location}
                    onChange={(e) => setSensorForm({ ...sensorForm, location: e.target.value })}
                    placeholder="Warehouse Zone A" />
                  
                </div>
                <div>
                  <Label>{t("temperatureZone", "Temperature Zone")}</Label>
                  <Select
                    value={sensorForm.zone_type}
                    onValueChange={(v) => {
                      const limits = {
                        frozen: { min: -25, max: -15 },
                        refrigerated: { min: 2, max: 8 },
                        cool: { min: 8, max: 15 },
                        ambient: { min: 15, max: 25 }
                      };
                      setSensorForm({
                        ...sensorForm,
                        zone_type: v,
                        min_temp: limits[v]?.min || 2,
                        max_temp: limits[v]?.max || 8
                      });
                    }}>
                    
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {Object.entries(ZONE_CONFIG).map(([key, config]) =>
                      <SelectItem key={key} value={key}>
                          <span className="flex items-center gap-2">
                            <span className={`w-2 h-2 rounded-full ${config.color}`} />
                            {config.label} ({config.range})
                          </span>
                        </SelectItem>
                      )}
                    </SelectContent>
                  </Select>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>{t("minTempC", "Min Temp (\xB0C)")}</Label>
                    <Input
                      type="number"
                      value={sensorForm.min_temp}
                      onChange={(e) => setSensorForm({ ...sensorForm, min_temp: parseFloat(e.target.value) })} />
                    
                  </div>
                  <div>
                    <Label>{t("maxTempC", "Max Temp (\xB0C)")}</Label>
                    <Input
                      type="number"
                      value={sensorForm.max_temp}
                      onChange={(e) => setSensorForm({ ...sensorForm, max_temp: parseFloat(e.target.value) })} />
                    
                  </div>
                </div>
                <div>
                  <Label>{t("alertEmailOptional", "Alert Email (optional)")}</Label>
                  <Input
                    value={sensorForm.alert_email}
                    onChange={(e) => setSensorForm({ ...sensorForm, alert_email: e.target.value })}
                    placeholder="alerts@company.com" />
                  
                </div>
                <Button onClick={handleCreateSensor} disabled={isLoading} className="w-full">
                  {isLoading ? 'Creating...' : 'Create Sensor'}
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Stats Cards */}
      {stats &&
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <Card>
            <CardContent className="pt-4">
              <div className="text-2xl font-bold">{stats.total_sensors}</div>
              <p className="text-sm text-muted-foreground">{t("totalSensors", "Total Sensors")}</p>
            </CardContent>
          </Card>
          <Card className="border-green-500/50">
            <CardContent className="pt-4">
              <div className="text-2xl font-bold text-green-500">{stats.in_range}</div>
              <p className="text-sm text-muted-foreground">{t("inRange", "In Range")}</p>
            </CardContent>
          </Card>
          <Card className="border-yellow-500/50">
            <CardContent className="pt-4">
              <div className="text-2xl font-bold text-yellow-500">{stats.warning}</div>
              <p className="text-sm text-muted-foreground">{t("warning", "Warning")}</p>
            </CardContent>
          </Card>
          <Card className="border-red-500/50">
            <CardContent className="pt-4">
              <div className="text-2xl font-bold text-red-500">{stats.out_of_range}</div>
              <p className="text-sm text-muted-foreground">{t("outOfRange", "Out of Range")}</p>
            </CardContent>
          </Card>
          <Card className="border-red-500/50">
            <CardContent className="pt-4">
              <div className="text-2xl font-bold text-red-500">{stats.active_excursions}</div>
              <p className="text-sm text-muted-foreground">{t("activeExcursions", "Active Excursions")}</p>
            </CardContent>
          </Card>
        </div>
      }

      {/* Active Excursions Alert */}
      {excursions.length > 0 &&
      <Card className="border-red-500 bg-red-50 dark:bg-red-950/20">
          <CardHeader className="pb-2">
            <CardTitle className="text-red-600 flex items-center gap-2">
              <AlertCircle className="h-5 w-5" />{t("activeTemperatureExcursions", "Active Temperature Excursions (")}
            {excursions.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {excursions.map((excursion) => {
              const sensor = sensors.find((s) => s.id === excursion.sensor_id);
              const severityConfig = SEVERITY_CONFIG[excursion.severity] || SEVERITY_CONFIG.warning;

              return (
                <div
                  key={excursion.id}
                  className="flex items-center justify-between p-3 bg-white dark:bg-slate-800 rounded-lg border">
                  
                    <div className="flex items-center gap-3">
                      <div className={`p-2 rounded-full ${severityConfig.color}`}>
                        <Thermometer className="h-4 w-4 text-white" />
                      </div>
                      <div>
                        <p className="font-medium">{sensor?.name || `Sensor ${excursion.sensor_id}`}</p>
                        <p className="text-sm text-muted-foreground">
                          {excursion.temperatures.min}{t("c", "\xB0C -")}{excursion.temperatures.max}{t("cLimit", "\xB0C\n                          (Limit:")}
                        {excursion.limits.min}{t("c", "\xB0C -")}{excursion.limits.max}{t("c", "\xB0C)")}
                      </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge className={severityConfig.color}>{severityConfig.label}</Badge>
                      {!excursion.acknowledged &&
                    <Button size="sm" variant="outline" onClick={() => acknowledgeExcursion(excursion.id)}>{t("acknowledge", "Acknowledge")}

                    </Button>
                    }
                    </div>
                  </div>);

            })}
            </div>
          </CardContent>
        </Card>
      }

      {/* Sensors Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {sensors.map((sensor) => {
          const status = getSensorStatus(sensor);
          const zoneConfig = ZONE_CONFIG[sensor.zone_type] || ZONE_CONFIG.refrigerated;
          const StatusIcon = status.icon;
          const ZoneIcon = zoneConfig.icon;

          const tempRange = sensor.limits.max - sensor.limits.min;
          const tempProgress = sensor.current?.temperature ?
          (sensor.current.temperature - sensor.limits.min) / tempRange * 100 :
          50;

          return (
            <Card
              key={sensor.id}
              className={`cursor-pointer hover:shadow-md transition-shadow ${sensor.in_excursion ? 'border-red-500 animate-pulse' : ''}`
              }
              onClick={() => {
                setSelectedSensor(sensor);
                loadSensorHistory(sensor.sensor_id);
              }}
              data-testid={`sensor-card-${sensor.sensor_id}`}>
              
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <div className={`p-2 rounded-lg ${zoneConfig.color}`}>
                      <ZoneIcon className="h-4 w-4 text-white" />
                    </div>
                    <div>
                      <CardTitle className="text-base">{sensor.name}</CardTitle>
                      <p className="text-xs text-muted-foreground flex items-center gap-1">
                        <MapPin className="h-3 w-3" />
                        {sensor.location || 'No location'}
                      </p>
                    </div>
                  </div>
                  <Badge className={status.color}>
                    <StatusIcon className="h-3 w-3 mr-1" />
                    {status.label}
                  </Badge>
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {/* Temperature */}
                  <div className="text-center">
                    <div className={`text-4xl font-bold ${sensor.in_excursion ? 'text-red-500' : ''}`
                    }>
                      {sensor.current?.temperature?.toFixed(1) || '--'}
                      <span className="text-xl">{t("c", "\xB0C")}</span>
                    </div>
                    <p className="text-xs text-muted-foreground">{t("limit", "Limit:")}
                      {sensor.limits.min}{t("c", "\xB0C -")}{sensor.limits.max}{t("c", "\xB0C")}
                    </p>
                  </div>

                  {/* Progress bar */}
                  <div className="relative pt-1">
                    <Progress
                      value={Math.max(0, Math.min(100, tempProgress))}
                      className={`h-2 ${sensor.in_excursion ? 'bg-red-200' : ''}`} />
                    
                    <div className="flex justify-between text-xs mt-1 text-muted-foreground">
                      <span>{sensor.limits.min}{t("c", "\xB0C")}</span>
                      <span>{sensor.limits.max}{t("c", "\xB0C")}</span>
                    </div>
                  </div>

                  {/* Additional metrics */}
                  <div className="grid grid-cols-3 gap-2 text-center text-xs">
                    <div className="p-2 bg-muted rounded">
                      <Droplets className="h-3 w-3 mx-auto mb-1 text-blue-500" />
                      <p className="font-medium">{sensor.current?.humidity?.toFixed(0) || '--'}%</p>
                      <p className="text-muted-foreground">{t("humidity", "Humidity")}</p>
                    </div>
                    <div className="p-2 bg-muted rounded">
                      <Battery className="h-3 w-3 mx-auto mb-1 text-green-500" />
                      <p className="font-medium">{sensor.current?.battery?.toFixed(0) || '--'}%</p>
                      <p className="text-muted-foreground">{t("battery", "Battery")}</p>
                    </div>
                    <div className="p-2 bg-muted rounded">
                      <Clock className="h-3 w-3 mx-auto mb-1 text-slate-500" />
                      <p className="font-medium">
                        {sensor.last_reading ?
                        new Date(sensor.last_reading).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) :
                        '--'
                        }
                      </p>
                      <p className="text-muted-foreground">{t("updated", "Updated")}</p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>);

        })}

        {sensors.length === 0 &&
        <Card className="col-span-full">
            <CardContent className="py-12 text-center text-muted-foreground">
              <Thermometer className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p>{t("noTemperatureSensors", "No temperature sensors configured")}</p>
              <p className="text-sm">{t("clickAddSensor", "Click \"Add Sensor\" to start monitoring")}</p>
            </CardContent>
          </Card>
        }
      </div>

      {/* Sensor Detail Dialog */}
      <Dialog open={!!selectedSensor} onOpenChange={() => setSelectedSensor(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Thermometer className="h-5 w-5" />
              {selectedSensor?.name}
            </DialogTitle>
          </DialogHeader>
          {selectedSensor &&
          <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-muted-foreground">{t("sensorId", "Sensor ID")}</Label>
                  <p className="font-medium">{selectedSensor.sensor_id}</p>
                </div>
                <div>
                  <Label className="text-muted-foreground">{t("location", "Location")}</Label>
                  <p className="font-medium">{selectedSensor.location || 'Not set'}</p>
                </div>
                <div>
                  <Label className="text-muted-foreground">{t("zoneType", "Zone Type")}</Label>
                  <Badge className={ZONE_CONFIG[selectedSensor.zone_type]?.color}>
                    {ZONE_CONFIG[selectedSensor.zone_type]?.label}
                  </Badge>
                </div>
                <div>
                  <Label className="text-muted-foreground">{t("temperatureRange", "Temperature Range")}</Label>
                  <p className="font-medium">{selectedSensor.limits.min}{t("c", "\xB0C -")}{selectedSensor.limits.max}{t("c", "\xB0C")}</p>
                </div>
              </div>

              <div className="border-t pt-4">
                <h4 className="font-medium mb-2 flex items-center gap-2">
                  <History className="h-4 w-4" />{t("recentHistory24h", "Recent History (24h)")}

              </h4>
                <ScrollArea className="h-[200px]">
                  <div className="space-y-1">
                    {sensorHistory.slice(0, 20).map((reading, idx) =>
                  <div
                    key={idx}
                    className={`flex items-center justify-between p-2 rounded text-sm ${reading.is_excursion ? 'bg-red-50 dark:bg-red-950/20' : 'bg-muted/50'}`
                    }>
                    
                        <span className={reading.is_excursion ? 'text-red-500 font-medium' : ''}>
                          {(reading.temperature ?? 0).toFixed(1)}{t("c", "\xB0C")}
                    </span>
                        <span className="text-muted-foreground text-xs">
                          {new Date(reading.recorded_at).toLocaleString()}
                        </span>
                      </div>
                  )}
                    {sensorHistory.length === 0 &&
                  <p className="text-center text-muted-foreground py-4">{t("noHistoryAvailable", "No history available")}</p>
                  }
                  </div>
                </ScrollArea>
              </div>
            </div>
          }
        </DialogContent>
      </Dialog>
    </div>);

};

export default ColdChain;
